// Medical Visit Models - Bulgarian Healthcare System

export interface Doctor {
  isSubstitute: boolean;      // H_S: 1/0
  uinNumber: string;          // UINH_S: "0500001066"
  fullName: string;           // FullNameH_S: "д-р Димитранка Диманова Кишева"
}

export interface Diagnosis {
  name: string;               // imeMD/imeD: diagnosis name
  mkbCode: string;           // MKB: ICD-10 code like "B34.1"
}

export interface ReimbursableDrug {
  drugCode: string;          // DrugCode: "NG028"
  mkbCode: string;           // DrugMKB: "F20.0"
  quantity: number;          // Quantity: 2
  days: number;              // Day: 30
}

export interface Therapy {
  nonReimbursable?: string;           // Nonreimburce: General therapy notes
  reimbursableDrugs?: ReimbursableDrug[];  // Reimbursible drugs array
}

export interface ProphylacticVisit {
  child: boolean;            // Child: 0/1
  mother: boolean;           // Mother: 0/1
  over18: boolean;           // Over18: 0/1
  risk: boolean;             // Risk: 0/1
}

export interface VisitType {
  consultation: boolean;      // Consult: 0/1
  dispensary: boolean;        // Disp: 0/1
  babyCare: boolean;         // Babycare: 0/1
  hospitalReferral: boolean;  // RpHosp: 0/1
  unfavorable: boolean;       // Unfav: 0/1
  lkkVisit: boolean;         // LKKVisit: 0/1
  telkVisit: boolean;        // Telk: 0/1
  prophylactic?: ProphylacticVisit;  // Profilact sub-types
}

export interface SickLeave {
  number: string;            // NoBl: "E20243121272"
  days: number;              // days: 3
  mkbCode: string;           // HLMKB: "B34.1"
  fromDate: string;          // HLFromDate: "2024-12-18"
  toDate: string;            // HLToDate: "2024-12-21"
  type: number;              // HLtype: 0
}

export interface Consultation {
  specialtyCode: string;     // kodSp: "01", "25", "18"
  consultFor: number;        // SpFor: 1, 2, 4
  number: string;            // NoN: "24353101DACD"
  mkbCode: string;           // SIMPMKB: "Z00.0"
}

export interface DiagnosticProcedure {
  number: string;            // NoMDD: "24353501D9F7"
  procedureFor: number;      // MDDFor: 7, 4, 1
  codes: string[];           // kodMDD: ["10.04", "01.01", "01.11"]
  mkbCode: string;           // MDDMKB: "Z00.0"
}

export interface PrescriptionDrug {
  prescriptionNumber: string; // prescNum: "2435140410C4"
  drugCode: string;          // DrugCode: "NG028"
  mkbCode: string;           // DrugMKB: "F20.0"
  quantity: number;          // Quantity: 2
  days: number;              // Day: 30
}

export interface Prescription {
  bookNumber: string;        // RpBook: "26525778"
  drugs: PrescriptionDrug[]; // Array of prescribed drugs
}

export interface MedicalDocuments {
  sickLeave?: SickLeave[];
  consultations?: Consultation[];
  diagnosticProcedures?: DiagnosticProcedure[];
  prescriptions?: Prescription[];
  telkForm: boolean;         // TalonTELK: 0/1
  notification: boolean;     // Izvestie: 0/1
  epicrisis: boolean;        // EtEpikriza: 0/1
  medicalNote: boolean;      // MedicalNote: 0/1
}

export interface MedicalVisit {
  id: string;
  
  // Visit Identification
  noAl: string;              // "24353A06510A" - Unique visit number
  visitDate: string;         // "2024-12-18" - dataAl
  visitTime: string;         // "06:40:00" - time
  paymentType: number;       // 1 - Pay
  
  // Patient Reference
  patientId: string;         // Foreign key to Patient
  
  // Doctor Information
  doctor: Doctor;
  
  // Special Cases
  disadvantaged: boolean;    // Disadv: 0/1
  incidentally: boolean;     // Incidentally: 0/1
  
  // Diagnoses
  mainDiagnosis: Diagnosis;
  additionalDiagnoses: Diagnosis[];
  
  // Clinical Notes
  anamnesis: string;         // Anamnesa: Patient complaints
  physicalExam: string;      // HState: Physical examination
  examinations?: string;     // Examine: Lab tests, ECG, etc.
  
  // Treatment
  therapy: Therapy;
  
  // Visit Classification
  visitTypeCode: string;     // NEW: Visit type code like 'TELK', 'CONSULTATION'
  examType: number;          // ExamType: 11, 12, 13
  
  // Documents
  documents: MedicalDocuments;
  
  // Visit Status
  signed: boolean;           // Sign: 0/1
  
  // System fields
  createdAt: string;
  updatedAt: string;
}

export interface CreateVisitRequest {
  patientId: string;
  noAl: string;
  visitDate: string;
  visitTime: string;
  paymentType: number;
  doctor: Doctor;
  disadvantaged: boolean;
  incidentally: boolean;
  mainDiagnosis: Diagnosis;
  additionalDiagnoses: Diagnosis[];
  anamnesis: string;
  physicalExam: string;
  examinations?: string;
  therapy: Therapy;
  visitTypeCode: string;
  examType: number;
  documents: MedicalDocuments;
  signed: boolean;
}

export interface UpdateVisitRequest extends Partial<CreateVisitRequest> {
  id: string;
}

// Helper functions for creating empty objects
export const createEmptyVisit = (): Omit<MedicalVisit, 'id' | 'createdAt' | 'updatedAt'> => ({
  noAl: '',
  visitDate: '',
  visitTime: '',
  paymentType: 1,
  patientId: '',
  doctor: {
    isSubstitute: false,
    uinNumber: '',
    fullName: ''
  },
  disadvantaged: false,
  incidentally: false,
  mainDiagnosis: {
    name: '',
    mkbCode: ''
  },
  additionalDiagnoses: [],
  anamnesis: '',
  physicalExam: '',
  examinations: '',
  therapy: {
    nonReimbursable: '',
    reimbursableDrugs: []
  },
  visitTypeCode: 'STANDARD',
  examType: 11,
  documents: {
    sickLeave: [],
    consultations: [],
    diagnosticProcedures: [],
    prescriptions: [],
    telkForm: false,
    notification: false,
    epicrisis: false,
    medicalNote: false
  },
  signed: false
});

// Common options for dropdowns
export const examTypeOptions = [
  { value: 11, label: "11 - Първичен преглед" },
  { value: 12, label: "12 - Контролен преглед" },
  { value: 13, label: "13 - Профилактичен преглед" }
];

export const paymentTypeOptions = [
  { value: 1, label: "1 - НЗОК" },
  { value: 2, label: "2 - Частно" },
  { value: 3, label: "3 - Договор" }
];

export const specialtyOptions = [
  { value: "01", label: "01 - Вътрешни болести" },
  { value: "05", label: "05 - Ендокринология" },
  { value: "18", label: "18 - Психиатрия" },
  { value: "25", label: "25 - Урология" }
];